<div class="flight-requests-view">
  @if (error()) { <div class="alert alert-danger" role="alert">{{ error() }}</div> }
  @if (message()) { <div class="alert alert-success" role="status">{{ message() }}</div> }

  <div class="header-row">
    <div>
      <h2>Flight Booking Requests</h2>
      <p>Approve or reject student flight bookings. A rejection always needs a reason, which the
        student sees. Export the queue to send to a booking company and import their confirmations
        when the tickets come back.</p>
    </div>
    <div class="header-actions">
      <button type="button" class="btn btn-secondary btn-sm" (click)="exportCsv()" [disabled]="busy()">
        Export CSV
      </button>
      <label class="btn btn-secondary btn-sm file-button">
        Import confirmations
        <input type="file" accept=".csv,text/csv" (change)="onImportFile($event)" hidden />
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card-header">Filter Requests</div>
    <div class="card-body">
      <form class="filter-bar" [formGroup]="filters" (ngSubmit)="applyFilters()">
        <label>
          Status
          <select class="form-control" formControlName="status">
            <option value="">All</option>
            @for (status of statuses; track status) {
              <option [value]="status">{{ status }}</option>
            }
          </select>
        </label>
        <label>
          Search
          <input class="form-control" formControlName="search" placeholder="Reference, student, city" />
        </label>
        <button type="submit" class="btn btn-primary btn-sm">Apply</button>
      </form>

      <div class="bulk-bar">
        <label class="checkbox-line">
          <input type="checkbox" [checked]="chosen().size > 0 && chosen().size === selectableIds().length"
            (change)="toggleAll()" />
          <span>Select submitted ({{ chosen().size }} selected)</span>
        </label>
        <div class="bulk-actions">
          <button type="button" class="btn btn-secondary btn-sm" (click)="bulkApprove()"
            [disabled]="busy() || !authorization.hasPermission(permissions.FlightRequestReview)">Approve selected</button>
          <button type="button" class="btn btn-danger btn-sm" (click)="bulkReject()"
            [disabled]="busy() || !authorization.hasPermission(permissions.FlightRequestReview)">Reject selected</button>
        </div>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card-header">
      Requests <span class="count-pill">{{ requests().length }}</span>
    </div>
    <div class="card-body" style="padding: 0;">
      <table class="table flight-requests-table">
        <thead>
          <tr>
            <th class="select-col"><span class="sr-only">Select</span></th>
            <th>Reference</th>
            <th>Student</th>
            <th>Route</th>
            <th>Departure</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr>
              <td colspan="7" class="empty-cell">Loading flight requests…</td>
            </tr>
          } @else if (requests().length === 0) {
            <tr>
              <td colspan="7" class="empty-cell">No flight requests match the current filters.</td>
            </tr>
          }
          @for (request of requests(); track request.id) {
            <tr>
              <td class="select-col">
                @if (canDecide(request.status)) {
                  <input type="checkbox" [checked]="isChosen(request.id)" (change)="toggleChosen(request.id)"
                    [attr.aria-label]="'Select ' + request.referenceNumber" />
                }
              </td>
              <td class="request-reference">
                <button type="button" class="reference-link" (click)="open(request)">{{ request.referenceNumber }}</button>
              </td>
              <td>
                <span class="student-cell">
                  <strong>{{ request.student.fullName }}</strong>
                  <span class="muted">{{ request.student.emailAddress || 'No email address' }}</span>
                </span>
              </td>
              <td>
                <span class="route-cell">
                  <strong>{{ request.departureCity }}</strong>
                  <span class="route-arrow">→</span>
                  <strong>{{ request.destinationCity }}</strong>
                </span>
                <span class="muted">
                  {{ request.optionCount }} option(s){{ request.requiresPaidFlight ? ' · paid flight' : '' }}
                </span>
              </td>
              <td>{{ request.requestedDepartureDate | date: 'dd MMM yyyy' }}</td>
              <td><span [class]="statusClass(request.status)">{{ request.status }}</span></td>
              <td class="action-cell">
                <button type="button" class="btn btn-secondary btn-sm" (click)="open(request)">View</button>
                @if (canDecide(request.status)) {
                  <button type="button" class="btn btn-primary btn-sm" (click)="approve(request)"
                    [disabled]="busy()">Approve</button>
                  <button type="button" class="btn btn-danger btn-sm" (click)="reject(request)"
                    [disabled]="busy()">Reject</button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <div class="card">
    <div class="card-header">Rejection Reason</div>
    <div class="card-body">
      <p class="hint">Required before rejecting — used for both single and bulk rejections.</p>
      <form class="decision-form" [formGroup]="decisionForm">
        <label>
          Reason
          <select class="form-control" formControlName="reasonCode">
            @for (code of reasonCodes; track code.value) {
              <option [value]="code.value">{{ code.label }}</option>
            }
          </select>
        </label>
        <label class="grow">
          Message to the student <span class="required">*</span>
          <textarea class="form-control" formControlName="reason"
            placeholder="Explain why the request was rejected."></textarea>
        </label>
      </form>
    </div>
  </div>

  @if (selected(); as detail) {
    <div class="card">
      <div class="card-header">
        Request {{ detail.referenceNumber }}
        <span class="count-pill">{{ detail.status }}</span>
        <button type="button" class="btn btn-secondary btn-sm header-close" (click)="close()">Close</button>
      </div>
      <div class="card-body">
        <section class="review-section">
          <h3>Student</h3>
          <dl class="details-grid">
            <div><dt>Name</dt><dd>{{ detail.student.fullName }}</dd></div>
            <div><dt>Email</dt><dd>{{ detail.student.emailAddress || 'Not provided' }}</dd></div>
            <div><dt>Phone</dt><dd>{{ detail.student.phoneNumber || 'Not provided' }}</dd></div>
            <div><dt>Identity number</dt><dd>{{ detail.student.identityNumber || 'Not provided' }}</dd></div>
          </dl>
        </section>

        <section class="review-section">
          <h3>Trip</h3>
          <dl class="details-grid">
            <div><dt>Departure</dt><dd>{{ detail.departureCity }}@if (detail.departureAirportCode) { ({{ detail.departureAirportCode }}) }</dd></div>
            <div><dt>Destination</dt><dd>{{ detail.destinationCity }}@if (detail.destinationAirportCode) { ({{ detail.destinationAirportCode }}) }</dd></div>
            <div><dt>Departure date</dt><dd>{{ detail.requestedDepartureDate | date: 'dd MMM yyyy' }}</dd></div>
            <div><dt>Return date</dt><dd>{{ detail.requestedReturnDate ? (detail.requestedReturnDate | date: 'dd MMM yyyy') : 'One way' }}</dd></div>
            <div><dt>Preferred cabin</dt><dd>{{ detail.preferredCabinClass || 'No preference' }}</dd></div>
            <div><dt>Paid flight</dt><dd>{{ detail.requiresPaidFlight ? 'Student expects to contribute' : 'Funded only' }}</dd></div>
            <div><dt>Purpose</dt><dd>{{ detail.purpose || 'Not provided' }}</dd></div>
            @if (detail.additionalNotes) {
              <div><dt>Notes</dt><dd>{{ detail.additionalNotes }}</dd></div>
            }
          </dl>
        </section>

        @if (detail.status === 'REJECTED') {
          <section class="review-section">
            <h3>Rejection</h3>
            <p class="rejection-note">
              <strong>{{ detail.decisionReasonCode || 'OTHER' }}.</strong> {{ detail.decisionReason }}
            </p>
          </section>
        }

        @if (detail.bookingReference) {
          <section class="review-section">
            <h3>Booking</h3>
            <dl class="details-grid">
              <div><dt>Reference</dt><dd>{{ detail.bookingReference }}</dd></div>
              <div><dt>Provider</dt><dd>{{ detail.bookingProvider || 'Not recorded' }}</dd></div>
              <div><dt>Ticket</dt><dd>{{ detail.ticketNumber || 'Not recorded' }}</dd></div>
              <div><dt>Cabin</dt><dd>{{ detail.bookedCabinClass || 'Not recorded' }}</dd></div>
              <div><dt>Cost</dt><dd>{{ detail.bookedCost ? (detail.bookedCost + ' ' + (detail.bookedCurrency || '')) : 'Not recorded' }}</dd></div>
            </dl>
          </section>
        }

        @if (canBook(detail) && authorization.hasPermission(permissions.FlightRequestBook)) {
          <section class="review-section">
            <h3>Attach booking information</h3>
            <form class="booking-form" [formGroup]="bookingForm" (ngSubmit)="attachBooking()">
              <label>
                Booking reference <span class="required">*</span>
                <input class="form-control" formControlName="bookingReference" />
              </label>
              <label>
                Provider
                <input class="form-control" formControlName="bookingProvider" />
              </label>
              <label>
                Ticket number
                <input class="form-control" formControlName="ticketNumber" />
              </label>
              <label>
                Cabin
                <input class="form-control" formControlName="cabinClass" />
              </label>
              <label>
                Cost
                <input type="number" class="form-control" formControlName="cost" />
              </label>
              <label>
                Currency
                <input class="form-control" formControlName="currency" />
              </label>
              <label>
                Travel date
                <input type="date" class="form-control" formControlName="travelDate" />
              </label>
              <label class="grow">
                Notes
                <textarea class="form-control" formControlName="notes"></textarea>
              </label>
              <div class="form-actions">
                <button type="submit" class="btn btn-primary btn-sm" [disabled]="busy()">Save booking</button>
              </div>
            </form>
          </section>
        }
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        Requested Flight Options <span class="count-pill">{{ detail.options.length }}</span>
      </div>
      <div class="card-body" style="padding: 0;">
        <table class="table flight-options-table">
          <thead>
            <tr>
              <th>Airline</th>
              <th>Flight</th>
              <th>Cabin</th>
              <th>Stops</th>
              <th>Estimated cost</th>
              <th>Preferred</th>
            </tr>
          </thead>
          <tbody>
            @if (detail.options.length === 0) {
              <tr>
                <td colspan="6" class="empty-cell">No flight options were provided.</td>
              </tr>
            }
            @for (option of detail.options; track option.id ?? $index) {
              <tr>
                <td class="option-airline">{{ option.airline || '—' }}</td>
                <td>{{ option.flightNumber || '—' }}</td>
                <td>{{ option.cabinClass || '—' }}</td>
                <td>{{ option.stops ?? '—' }}</td>
                <td><span class="cost-cell">{{ option.estimatedCost ?? '—' }} {{ option.estimatedCost ? (option.currency || '') : '' }}</span></td>
                <td>
                  <span class="badge" [class.badge-success]="option.isPreferred"
                    [class.badge-muted]="!option.isPreferred">
                    {{ option.isPreferred ? 'Preferred' : 'Alternative' }}
                  </span>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }

  @if (importResult(); as imported) {
    <div class="card">
      <div class="card-header">Import Result</div>
      <div class="card-body">
        <p>{{ imported.booked }} booked, {{ imported.skipped }} skipped, out of {{ imported.totalRows }} row(s).</p>
        @if (imported.errors.length > 0) {
          <ul class="import-errors">
            @for (issue of imported.errors; track $index) {
              <li>Line {{ issue.lineNumber }}: {{ issue.message }}</li>
            }
          </ul>
        }
      </div>
    </div>
  }
</div>
